import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, ArrowRight, Mail } from "lucide-react";
import { motion } from "framer-motion";
import MarketingNav from "@/components/MarketingNav";
import MarketingFooter from "@/components/MarketingFooter";
import { api } from "@/lib/api";
import { useT, useLang } from "@/lib/i18n";

const FEATURE_KEYS = {
  trial: ["plan.trial.f1", "plan.trial.f2", "plan.trial.f3", "plan.trial.f4", "plan.trial.f5"],
  starter: ["plan.starter.f1", "plan.starter.f2", "plan.starter.f3", "plan.starter.f4", "plan.starter.f5", "plan.starter.f6"],
  professional: ["plan.pro.f1", "plan.pro.f2", "plan.pro.f3", "plan.pro.f4", "plan.pro.f5", "plan.pro.f6", "plan.pro.f7"],
  enterprise: ["plan.ent.f1", "plan.ent.f2", "plan.ent.f3", "plan.ent.f4", "plan.ent.f5", "plan.ent.f6", "plan.ent.f7"],
};

function formatPrice(plan, lang) {
  if (plan.price_brl === null || plan.price_usd === null) return null;
  if (lang === "pt") return { sym: "R$ ", value: plan.price_brl?.toLocaleString("pt-BR") };
  return { sym: "$", value: plan.price_usd?.toLocaleString("en-US") };
}

// Per-cycle multiplier on the base monthly price (mirrors backend BILLING_CYCLES).
const CYCLES = {
  monthly:    { mult: 1,   off: null,   label: { pt: "Mensal", en: "Monthly" },    billed: { pt: "pago mensalmente", en: "billed monthly" } },
  semiannual: { mult: 0.9, off: "-10%", label: { pt: "Semestral", en: "Semiannual" }, billed: { pt: "pago semestralmente", en: "billed semiannually" } },
  annual:     { mult: 0.8, off: "-20%", label: { pt: "Anual", en: "Annual" },      billed: { pt: "pago anualmente", en: "billed annually" } },
};

export default function Pricing() {
  const t = useT();
  const { lang } = useLang();
  const [data, setData] = useState({ plans: [], pay_per_use: null, discounts: {} });
  const [cycle, setCycle] = useState("monthly");

  useEffect(() => { api.get("/plans").then(({ data }) => setData(data)); }, []);

  return (
    <div className="min-h-screen bg-background">
      <MarketingNav />
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-20 text-center">
        <div className="overline mb-4">// pricing</div>
        <h1 className="font-display font-black text-5xl lg:text-6xl tracking-tighter">{t("pricing.h1")}</h1>
        <p className="mt-6 text-white/60 max-w-2xl mx-auto">{t("pricing.subtitle")}</p>

        {/* Billing cycle toggle */}
        <div className="inline-flex items-center mt-10 border border-white/10 font-mono text-xs" data-testid="pricing-cycle-toggle">
          {["monthly", "semiannual", "annual"].map((c) => (
            <button key={c} onClick={() => setCycle(c)} data-testid={`pricing-cycle-${c}`}
              className={`px-4 py-2 transition ${cycle === c ? "bg-white/10 text-white" : "text-white/50 hover:text-white"}`}>
              {CYCLES[c].label[lang === "pt" ? "pt" : "en"]}
              {CYCLES[c].off && <span className="text-primary ml-1">{CYCLES[c].off}</span>}
            </button>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-8 pb-16">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {data.plans.map((plan, i) => {
            const featured = plan.id === "professional";
            const keys = FEATURE_KEYS[plan.id] || [];
            const p = formatPrice(plan, lang);
            const cycleValue = p && plan.price_brl ?
              ((lang === "pt" ? plan.price_brl : plan.price_usd) * CYCLES[cycle].mult)
                .toLocaleString(lang === "pt" ? "pt-BR" : "en-US", {maximumFractionDigits: 0}) : null;
            return (
              <motion.div
                key={plan.id}
                initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.06 }}
                className={`surface p-8 flex flex-col ${featured ? "border-primary ring-1 ring-primary/40" : ""}`}
                data-testid={`plan-card-${plan.id}`}
              >
                {featured && <div className="overline text-primary mb-3">{t("pricing.popular")}</div>}
                <div className="overline mb-2">{plan.id.toUpperCase()}</div>
                <h3 className="font-display font-black text-3xl tracking-tighter">{plan.name}</h3>
                <p className="text-xs text-white/50 mt-1 mb-4 font-mono">{plan.tagline?.[lang]}</p>
                <div className="mt-2 mb-6">
                  {p === null ? (
                    <div className="font-mono text-3xl">Custom</div>
                  ) : p.value === "0" ? (
                    <div className="flex items-baseline gap-1">
                      <span className="font-mono text-4xl font-bold">{lang === "pt" ? "Grátis" : "Free"}</span>
                    </div>
                  ) : cycle !== "monthly" ? (
                    <div>
                      <div className="flex items-baseline gap-1">
                        <span className="font-mono text-4xl font-bold">{p.sym}{cycleValue}</span>
                        <span className="text-white/40 text-sm">/{lang === "pt" ? "mês" : "mo"}</span>
                      </div>
                      <div className="text-xs text-white/40 font-mono mt-1">
                        <span className="line-through">{p.sym}{p.value}</span> · {CYCLES[cycle].billed[lang === "pt" ? "pt" : "en"]}
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-baseline gap-1">
                      <span className="font-mono text-4xl font-bold">{p.sym}{p.value}</span>
                      <span className="text-white/40 text-sm">/{lang === "pt" ? "mês" : "mo"}</span>
                    </div>
                  )}
                </div>
                <div className="text-xs font-mono text-white/40 space-y-1 mb-4 pb-4 border-b border-white/5">
                  <div>{lang === "pt" ? "Cards" : "Cards"}: {plan.cards_limit === -1 ? "∞" : `${plan.cards_limit}/${lang === "pt" ? "mês" : "mo"}`}</div>
                  <div>{lang === "pt" ? "Membros" : "Members"}: {plan.members_limit === -1 ? "∞" : plan.members_limit}</div>
                  <div>{lang === "pt" ? "Squads" : "Squads"}: {plan.squads_limit === -1 ? "∞" : plan.squads_limit}</div>
                </div>
                <ul className="space-y-2.5 mb-8 text-sm">
                  {keys.map((k) => (
                    <li key={k} className="flex items-start gap-2 text-white/80">
                      <Check className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                      <span>{t(k)}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-auto">
                  {plan.id === "trial" && (
                    <Link to="/signup" className="btn-primary w-full" data-testid={`cta-${plan.id}`}>{t("pricing.startFree")} <ArrowRight className="w-4 h-4" /></Link>
                  )}
                  {plan.id === "starter" && (
                    <Link to={`/signup?plan=starter&cycle=${cycle}`} className="btn-ghost w-full" data-testid={`cta-${plan.id}`}>{t("pricing.chooseStarter")}</Link>
                  )}
                  {plan.id === "professional" && (
                    <Link to={`/signup?plan=professional&cycle=${cycle}`} className="btn-primary w-full" data-testid={`cta-${plan.id}`}>{lang === "pt" ? "Escolher Pro" : "Choose Pro"} <ArrowRight className="w-4 h-4" /></Link>
                  )}
                  {plan.id === "enterprise" && (
                    <a href="mailto:sales@ratchet.dev" className="btn-ghost w-full" data-testid={`cta-${plan.id}`}>{t("pricing.contactSales")} <Mail className="w-4 h-4" /></a>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Pay-per-use alternative */}
        {data.pay_per_use && (
          <div className="mt-10 surface p-8 grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="pay-per-use-band">
            <div className="md:col-span-1">
              <div className="overline text-primary mb-2">{lang === "pt" ? "Modelo alternativo" : "Alternative model"}</div>
              <h3 className="font-display font-black text-2xl tracking-tighter">{lang === "pt" ? "Pay-per-use (freemium)" : "Pay-per-use (freemium)"}</h3>
              <p className="text-sm text-white/60 mt-2">{lang === "pt"
                ? "Para times que querem começar sem compromisso e crescer por uso."
                : "For teams who want to start without commitment and grow by usage."}</p>
            </div>
            <div className="md:col-span-2 grid grid-cols-2 md:grid-cols-4 gap-4 font-mono text-xs">
              <Stat label={lang === "pt" ? "Devs grátis" : "Free devs"} value={`${data.pay_per_use.free_devs}`} />
              <Stat label={lang === "pt" ? "Análises grátis/mês" : "Free analyses/mo"} value={`${data.pay_per_use.free_analyses_month}`} />
              <Stat label={lang === "pt" ? "Análise extra" : "Extra analysis"}
                value={lang === "pt" ? `R$ ${data.pay_per_use.extra_analysis_brl}` : `$${data.pay_per_use.extra_analysis_usd}`} />
              <Stat label={lang === "pt" ? "Dev extra/mês" : "Extra dev/mo"}
                value={lang === "pt" ? `R$ ${data.pay_per_use.extra_dev_brl}` : `$${data.pay_per_use.extra_dev_usd}`} />
            </div>
          </div>
        )}

        {/* Discounts */}
        {Object.keys(data.discounts || {}).length > 0 && (
          <div className="mt-10">
            <div className="overline mb-3">{lang === "pt" ? "// descontos disponíveis" : "// available discounts"}</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {Object.entries(data.discounts).map(([key, d]) => (
                <div key={key} className="surface p-4 text-xs font-mono" data-testid={`discount-${key}`}>
                  <div className="text-primary text-2xl font-bold mb-1">
                    {d.percent ? `-${d.percent}%` : (lang === "pt" ? "1 mês" : "1 mo")}
                  </div>
                  <div className="text-white/60">{d.label[lang]}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            [t("pricing.band1.title"), t("pricing.band1.desc")],
            [t("pricing.band2.title"), t("pricing.band2.desc")],
            [t("pricing.band3.title"), t("pricing.band3.desc")],
          ].map(([title, desc]) => (
            <div key={title} className="surface p-6">
              <h4 className="font-display font-bold text-lg mb-1">{title}</h4>
              <p className="text-sm text-white/60">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-6 pb-32">
        <div className="overline mb-4">// faq</div>
        <h2 className="font-display font-black text-3xl tracking-tighter mb-8">{t("pricing.faq.title")}</h2>
        <div className="space-y-3">
          {[
            [t("pricing.faq.q1"), t("pricing.faq.a1")],
            [t("pricing.faq.q2"), t("pricing.faq.a2")],
            [t("pricing.faq.q3"), t("pricing.faq.a3")],
            [t("pricing.faq.q4"), t("pricing.faq.a4")],
          ].map(([q, a], i) => (
            <details key={i} className="surface p-5 group">
              <summary className="cursor-pointer flex justify-between items-center font-medium">
                <span>{q}</span>
                <span className="font-mono text-primary group-open:rotate-45 transition-transform">+</span>
              </summary>
              <p className="mt-3 text-sm text-white/60 leading-relaxed">{a}</p>
            </details>
          ))}
        </div>
      </section>
      <MarketingFooter />
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="text-white text-xl font-bold mb-1">{value}</div>
      <div className="text-white/40">{label}</div>
    </div>
  );
}
